import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import type { SelectChangeEvent } from '@mui/material';
import { Box, MenuItem, Select, Stack, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { format } from 'date-fns';

import type {
  ActionItemStatusEnum,
  Filter,
  FollowUpAction,
} from 'src/types/myFollowUp';
import { IMPLEMENTATION_STATUS_CHOICES } from 'src/components/myFollowUp/MyFollowUpPage/FollowUpOverview/constants';
import useFollowUpDetail from 'src/components/myFollowUp/MyFollowUpPage/FollowUpOverview/FollowUpDetailModal/FollowUpDetailContext';

import type { ActionPlanContent } from './components/ActionPlan';
import ActionPlan from './components/ActionPlan';

import { styles } from './index.styles';

interface MyFollowUpProps {
  actionItem: FollowUpAction;
  selectedKpi?: Filter;
  disabled?: boolean;
}

const MyFollowUp: FC<MyFollowUpProps> = ({
  actionItem,
  selectedKpi,
  disabled = false,
}) => {
  const { t } = useTranslation('myFollowUp');
  const { myFollowUpDeadline, updateFormData, mutationError } =
    useFollowUpDetail();

  const defaultDeadline = useMemo(
    () => (actionItem.dueAt ? new Date(actionItem.dueAt) : myFollowUpDeadline),
    [actionItem.dueAt, myFollowUpDeadline],
  );

  const [dueDate, setDueDate] = useState(defaultDeadline);

  useEffect(() => {
    if (defaultDeadline) {
      setDueDate(defaultDeadline);
      updateFormData({
        dueAt: format(defaultDeadline, 'dd.MM.yyyy'),
      });
    }
  }, [defaultDeadline]);

  const [open, setOpen] = useState(false);
  const [implementationStatus, setImplementationStatus] =
    useState<ActionItemStatusEnum>(actionItem.implementationStatus ?? 0);

  const handleDateChange = useCallback(
    (date: Date | null) => {
      const newDate =
        date && myFollowUpDeadline && date > myFollowUpDeadline
          ? myFollowUpDeadline
          : date || undefined;

      setDueDate(newDate);
      updateFormData({
        dueAt: newDate ? format(newDate, 'dd.MM.yyyy') : undefined,
      });
    },
    [myFollowUpDeadline, setDueDate, updateFormData],
  );

  const handleStatusChange = useCallback(
    (event: SelectChangeEvent<number>) => {
      const value = Number(event.target.value) as ActionItemStatusEnum;
      setImplementationStatus(value);
      updateFormData({ implementationStatus: value });
    },
    [setImplementationStatus, updateFormData],
  );

  const handleActionPlanChange = useCallback(
    (content: ActionPlanContent) => {
      updateFormData({
        actionPlanTitle: content.title,
        actionPlanDetails: content.details,
        actionPlanAdditionalResources: content.additionalResources,
      });
    },
    [updateFormData],
  );

  return (
    <Stack spacing={3}>
      <Stack direction="row" spacing={2} sx={{ pt: 1, gap: 7 }}>
        <Box sx={{ flexGrow: 0, width: '50%' }}>
          <Typography variant="h6">
            {t('actionItemHeader.implementationDeadline')}:
          </Typography>
          <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
            {disabled || (
              <CalendarMonthOutlinedIcon
                sx={{ cursor: 'pointer' }}
                onClick={() => setOpen(true)}
              />
            )}
            <DatePicker
              value={dueDate}
              onChange={handleDateChange}
              maxDate={myFollowUpDeadline}
              views={['year', 'month', 'day']}
              format="dd.MM.yyyy"
              disabled={disabled}
              slots={{
                openPickerButton: undefined,
              }}
              open={open}
              onOpen={() => setOpen(true)}
              onClose={() => setOpen(false)}
              slotProps={{
                textField: {
                  onClick: () => setOpen(true),
                  size: 'small',
                  sx: styles.pickerField,
                },
              }}
            />
          </Stack>
          {mutationError?.dueAt && (
            <Box sx={styles.error}>{mutationError.dueAt}</Box>
          )}
        </Box>
        <Box sx={{ flexGrow: 0, width: '50%' }}>
          <Typography variant="h6">
            {t('actionItemHeader.implementationStatus')}
          </Typography>
          <Select
            disabled={disabled}
            value={implementationStatus}
            onChange={handleStatusChange}
            sx={styles.dropdown}
            MenuProps={{
              PaperProps: {
                sx: styles.selectMenu,
              },
            }}
          >
            {IMPLEMENTATION_STATUS_CHOICES.map(([value, label]) => (
              <MenuItem key={value} value={value}>
                {value == -1 ? t('actionItem.discontinued') : label}
              </MenuItem>
            ))}
          </Select>
        </Box>
      </Stack>
      {selectedKpi && (
        <Box>
          <ActionPlan
            disabled={disabled}
            actionItem={actionItem}
            kpi={selectedKpi}
            onContentChange={handleActionPlanChange}
          />
        </Box>
      )}
    </Stack>
  );
};

export default MyFollowUp;
